import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { passwordSchema, resetPasswordSchema } from '@shared/schemas/auth';
import { setInitialPassword } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const inputAuthClass =
  'h-12 rounded-xl border-zinc-200/90 bg-white text-base shadow-none transition-colors placeholder:text-zinc-400 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 dark:border-zinc-700 dark:bg-zinc-900/80 dark:placeholder:text-zinc-500';

const labelAuthClass = 'text-xs font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400';

const setPasswordFormSchema = resetPasswordSchema.extend({
  confirmPassword: passwordSchema,
}).refine((values) => values.newPassword === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

type SetPasswordFormValues = z.infer<typeof setPasswordFormSchema>;

export function SetPasswordForm({
  className,
  ...props
}: React.ComponentProps<'form'>) {
  const navigate = useNavigate();
  const form = useForm<SetPasswordFormValues>({
    defaultValues: {
      confirmPassword: '',
      newPassword: '',
    },
    resolver: zodResolver(setPasswordFormSchema),
  });

  async function handleSubmit(values: SetPasswordFormValues) {
    form.clearErrors('root');

    const result = await setInitialPassword(values.newPassword);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    toast.success('Mot de passe défini. Session active.');
    navigate('/', { replace: true });
  }

  return (
    <form className={cn('flex flex-col gap-0', className)} onSubmit={form.handleSubmit(handleSubmit)} {...props}>
      <FieldGroup className="gap-6">
        <div className="space-y-2 pb-1">
          <h1 className="text-balance text-3xl font-semibold leading-tight tracking-tight text-zinc-950 sm:text-[1.875rem] dark:text-zinc-50">
            Finalisez votre accès.
          </h1>
          <p className="text-pretty text-base leading-relaxed text-zinc-500 dark:text-zinc-400">
            Votre adresse e-mail est confirmée et votre session est ouverte. Choisissez maintenant le mot de passe qui servira à vos prochaines connexions.
          </p>
        </div>
        <Field className="gap-2">
          <FieldLabel htmlFor="set-password" className={labelAuthClass}>Mot de passe</FieldLabel>
          <Input
            id="set-password"
            data-testid="set-password-input"
            type="password"
            autoComplete="new-password"
            className={inputAuthClass}
            aria-invalid={form.formState.errors.newPassword ? 'true' : 'false'}
            {...form.register('newPassword')}
          />
          <FieldDescription>Au moins 8 caractères.</FieldDescription>
          <FieldError errors={[form.formState.errors.newPassword]} />
        </Field>
        <Field className="gap-2">
          <FieldLabel htmlFor="set-password-confirmation" className={labelAuthClass}>Confirmation du mot de passe</FieldLabel>
          <Input
            id="set-password-confirmation"
            data-testid="set-password-confirmation-input"
            type="password"
            autoComplete="new-password"
            className={inputAuthClass}
            aria-invalid={form.formState.errors.confirmPassword ? 'true' : 'false'}
            {...form.register('confirmPassword')}
          />
          <FieldError errors={[form.formState.errors.confirmPassword]} />
        </Field>
        <FieldError errors={[form.formState.errors.root]} />
        <Field className="pt-1">
          <Button
            data-testid="set-password-submit"
            type="submit"
            size="lg"
            className="h-12 w-full rounded-xl text-base font-semibold shadow-sm"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <Loader2Icon className="size-5 animate-spin" /> : null}
            Définir mon mot de passe
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
